import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

const STORAGE_KEY = 'v2-ui-state';

type QueuedToastType = 'success' | 'error' | 'info';

type QueuedToast = {
  id: string;
  message: string;
  type: QueuedToastType;
  createdAt: number;
};

type PersistedUIState = {
  sidebarCollapsed: boolean;
  userPreferences: Record<string, unknown>;
  listColumnsByEntity: Record<string, string[]>;
};

type UIStateContextValue = {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebarCollapsed: () => void;
  userPreferences: Record<string, unknown>;
  setUserPreference: (key: string, value: unknown) => void;
  setUserPreferences: (preferences: Record<string, unknown>) => void;
  toastQueue: QueuedToast[];
  enqueueToast: (toast: Omit<QueuedToast, 'id' | 'createdAt'>) => string;
  removeToast: (id: string) => void;
  clearToastQueue: () => void;
  getEntityVisibleColumns: (entityName: string) => string[];
  setEntityVisibleColumns: (entityName: string, columnKeys: string[]) => void;
};

const defaultPersistedState: PersistedUIState = {
  sidebarCollapsed: false,
  userPreferences: {},
  listColumnsByEntity: {},
};

const UIStateContext = createContext<UIStateContextValue | null>(null);

const sanitizeColumnKeys = (columnKeys: string[]) => Array.from(new Set(columnKeys.filter(Boolean)));

const loadPersistedState = (): PersistedUIState => {
  if (typeof window === 'undefined') return defaultPersistedState;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultPersistedState;

    const parsed = JSON.parse(raw) as Partial<PersistedUIState>;
    const sidebarCollapsed = typeof parsed.sidebarCollapsed === 'boolean'
      ? parsed.sidebarCollapsed
      : defaultPersistedState.sidebarCollapsed;
    const userPreferences = parsed.userPreferences && typeof parsed.userPreferences === 'object'
      ? parsed.userPreferences
      : defaultPersistedState.userPreferences;
    const listColumnsByEntity = Object.fromEntries(
      Object.entries(parsed.listColumnsByEntity ?? {}).map(([entityName, columnKeys]) => [
        entityName,
        Array.isArray(columnKeys) ? sanitizeColumnKeys(columnKeys.map((value) => String(value))) : [],
      ]),
    );

    return {
      sidebarCollapsed,
      userPreferences,
      listColumnsByEntity,
    };
  } catch {
    return defaultPersistedState;
  }
};

const buildToastId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

type UIStateProviderProps = {
  children: ReactNode;
};

export const UIStateProvider = ({ children }: UIStateProviderProps) => {
  const [persistedState, setPersistedState] = useState<PersistedUIState>(() => loadPersistedState());
  const [toastQueue, setToastQueue] = useState<QueuedToast[]>([]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(persistedState));
  }, [persistedState]);

  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    setPersistedState((prev) => ({
      ...prev,
      sidebarCollapsed: collapsed,
    }));
  }, []);

  const toggleSidebarCollapsed = useCallback(() => {
    setPersistedState((prev) => ({
      ...prev,
      sidebarCollapsed: !prev.sidebarCollapsed,
    }));
  }, []);

  const setUserPreference = useCallback((key: string, value: unknown) => {
    setPersistedState((prev) => ({
      ...prev,
      userPreferences: {
        ...prev.userPreferences,
        [key]: value,
      },
    }));
  }, []);

  const setUserPreferences = useCallback((preferences: Record<string, unknown>) => {
    setPersistedState((prev) => ({
      ...prev,
      userPreferences: {
        ...prev.userPreferences,
        ...preferences,
      },
    }));
  }, []);

  const enqueueToast = useCallback((toast: Omit<QueuedToast, 'id' | 'createdAt'>) => {
    const id = buildToastId();
    setToastQueue((prev) => [
      ...prev,
      {
        id,
        createdAt: Date.now(),
        ...toast,
      },
    ]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToastQueue((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearToastQueue = useCallback(() => {
    setToastQueue([]);
  }, []);

  const getEntityVisibleColumns = useCallback((entityName: string) => {
    return persistedState.listColumnsByEntity[entityName] ?? [];
  }, [persistedState.listColumnsByEntity]);

  const setEntityVisibleColumns = useCallback((entityName: string, columnKeys: string[]) => {
    const normalizedColumnKeys = sanitizeColumnKeys(columnKeys);
    setPersistedState((prev) => ({
      ...prev,
      listColumnsByEntity: {
        ...prev.listColumnsByEntity,
        [entityName]: normalizedColumnKeys,
      },
    }));
  }, []);

  const value = useMemo<UIStateContextValue>(() => ({
    sidebarCollapsed: persistedState.sidebarCollapsed,
    setSidebarCollapsed,
    toggleSidebarCollapsed,
    userPreferences: persistedState.userPreferences,
    setUserPreference,
    setUserPreferences,
    toastQueue,
    enqueueToast,
    removeToast,
    clearToastQueue,
    getEntityVisibleColumns,
    setEntityVisibleColumns,
  }), [
    clearToastQueue,
    enqueueToast,
    getEntityVisibleColumns,
    persistedState.sidebarCollapsed,
    persistedState.userPreferences,
    removeToast,
    setEntityVisibleColumns,
    setSidebarCollapsed,
    setUserPreference,
    setUserPreferences,
    toastQueue,
    toggleSidebarCollapsed,
  ]);

  return (
    <UIStateContext.Provider value={value}>
      {children}
    </UIStateContext.Provider>
  );
};

export const useUIState = () => {
  const context = useContext(UIStateContext);
  if (!context) {
    throw new Error('useUIState must be used within UIStateProvider');
  }
  return context;
};
